/**
 * Публичный каталог для клиентского wizard (E07, ФТ-1.1–1.2).
 * Только опубликованные записи; без админ-полей (status, цены, формулы).
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";
import { decimalStringSchema } from "./catalog";

const nameSchema = z.string().trim().min(1).max(255);
const sortOrderSchema = z.number().int().min(0).max(100_000);

/** Отрасль на шаге 1 wizard (ФТ-1.1). */
export const publicIndustrySchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  sortOrder: sortOrderSchema,
});

export type PublicIndustry = z.infer<typeof publicIndustrySchema>;

export const publicIndustryListResponseSchema = apiSuccessSchema(z.array(publicIndustrySchema));

/** Типоразмер на карточке модели (ФТ-1.2). */
export const publicUnitSummarySchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(128),
});

export type PublicUnitSummary = z.infer<typeof publicUnitSummarySchema>;

/**
 * Метрика карточки из констант типоразмеров (scope=unit, show_to_client).
 * Диапазон — min/max по опубликованным типоразмерам; для «макс.» UI берёт max.
 */
export const publicModelMetricSchema = z.object({
  attributeId: z.string().uuid(),
  name: nameSchema,
  formulaName: z.string().min(1).max(128).nullable(),
  unitName: nameSchema.nullable(),
  min: decimalStringSchema.nullable(),
  max: decimalStringSchema.nullable(),
});

export type PublicModelMetric = z.infer<typeof publicModelMetricSchema>;

/** Карточка модели на шаге 2 wizard (ФТ-1.2). */
export const publicModelCardSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  groupId: z.string().uuid(),
  groupName: nameSchema,
  groupSortOrder: sortOrderSchema,
  industries: z.array(publicIndustrySchema),
  units: z.array(publicUnitSummarySchema),
  metrics: z.array(publicModelMetricSchema),
});

export type PublicModelCard = z.infer<typeof publicModelCardSchema>;

export const publicModelCardListResponseSchema = apiSuccessSchema(z.array(publicModelCardSchema));

/** Пункт выпадающего списка на форме параметров (ФТ-1.5.1). */
export const publicFormOptionSchema = z.object({
  value: z.string().min(1).max(512),
  sortOrder: sortOrderSchema,
});

export type PublicFormOption = z.infer<typeof publicFormOptionSchema>;

/**
 * Поле формы параметров (ФТ-1.5).
 * Формулы и условия показа клиенту не отдаются (И-1).
 */
export const publicFormFieldSchema = z.object({
  id: z.string().uuid(),
  attributeId: z.string().uuid(),
  name: nameSchema,
  formulaName: z.string().min(1).max(128).nullable(),
  valueType: z.enum(["number", "text", "list"]),
  unitName: nameSchema.nullable(),
  groupId: z.string().uuid().nullable(),
  groupName: nameSchema.nullable(),
  sortOrder: sortOrderSchema,
  inputType: z.enum(["list", "manual", "calculated"]),
  isRequired: z.boolean(),
  allowManualOverride: z.boolean(),
  defaultValue: z.string().max(512).nullable(),
  options: z.array(publicFormOptionSchema),
});

export type PublicFormField = z.infer<typeof publicFormFieldSchema>;

/** Форма параметров опубликованной модели (ФТ-1.5). */
export const publicModelFormSchema = z.object({
  modelId: z.string().uuid(),
  modelName: nameSchema,
  fields: z.array(publicFormFieldSchema),
});

export type PublicModelForm = z.infer<typeof publicModelFormSchema>;

export const publicModelFormResponseSchema = apiSuccessSchema(publicModelFormSchema);
